import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useMediaQuery } from 'usehooks-ts';

import {
  Active,
  AutoScrollActivator,
  defaultDropAnimationSideEffects,
  DndContext,
  DropAnimation,
  PointerSensor,
  pointerWithin,
  TouchSensor,
  TraversalOrder,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { addSeconds, format, isValid } from 'date-fns';
import { DayPart, PlanAppointmentsRequest, ScheduleResponse } from 'generated';
import { useElementSize, useNavParams, useSelector } from 'hooks';
import { useRecoilState, useRecoilValue } from 'recoil';

import { Event, PlanningEntityMap } from '../types';
import { EditAppointmentType } from 'RestAPIs/activities/types';

import {
  APNotification,
  findItem,
  appendCorrelationToken,
  getConflictingEvents,
  notificationsToHashTable,
  planningEntitiesToEvents,
  handlePlanAppointments as handlePlanAppointmentsUtil,
  unassignAppointment as unassignAppointmentUtil
} from '../utils';
import {
  getSecondsPerPixel,
  getStartingPoint,
  getTimeBarItemWidth
} from './utils';

import { ConnectAppointmentGroupingPayload } from 'RestAPIs/activities';
import { mutations } from 'RestAPIs/rapini';

import {
  dialogManagerActiveAtom,
  dragActionActiveAtom,
  isToPlanDrawerOpenAtom,
  planResponsePlanningEntityIdentifiersAtom,
  selectedDayPartsAtom,
  selectedGroupingScheduleAtom,
  shouldShowPopoverDialogAtom
} from '../atoms';
import { appointmentDropPositionXAtom } from './atoms';

import { Box, Stack } from '@mui/material';

import { LoadingOverlay, showSnackbar } from 'components/shared';

import { DialogType } from '../components/DialogManager/DialogManager';

import { UsePlanningReturn } from '../hooks/usePlanning';
import useTimelineInterval from '../hooks/useTimelineInterval';
import TimelineRoute from './TimelineRoute';
import Summary from './TimelineRoute/Summary';
import usePlanningEntity from '../hooks/usePlanningEntity';
import { TimelineBar, ZoomLevel } from './TimelineBar';
import { TIMELINE_SUMMARY_WIDTH } from './TimelineRoute/Summary/Summary';
import { GRAY, GRAY_LIGHT_SECONDARY } from 'constants/colors';
import { Period } from 'components/shared/DateNavigation/DateNavigation';
import { dateNavigationPeriodAtom } from 'components/shared/DateNavigation/atoms';
import { PopOverConfirmationContext } from '../PopOverConfirmationContext';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setUserPreference } from '../../../../redux/actionCreators';
import { View } from '../enums';
import TaskList, { TASK_LIST_WIDTH } from 'components/shared/TaskList/TaskList';
import DragPortal from '../components/DraggableOverlay/DragPortal';

const TIMELINE_DRAGGABLE_SCROLL_CONTAINER_CLASS =
  '__TIMELINE_DRAGGABLE_SCROLL_CONTAINER_CLASS__';

export default function Timeline({
  startDate,
  planningEntities,
  planAppointments,
  connectAppointmentGrouping,
  revertAction,
  isLoading,
  showEvents
}: UsePlanningReturn) {
  const dispatch = useDispatch();
  const { defaultDayTimelineZoomLevel, defaultWeekTimelineZoomLevel } =
    useSelector((state) => state.preferences);

  const [, setDialogState] = useRecoilState(dialogManagerActiveAtom);
  const [taskListIsOpen, setTaskListIsOpen] = useState<boolean>(true);

  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );

  const [isToPlanDrawerOpen] = useRecoilState(isToPlanDrawerOpenAtom);
  const showToPlanDrawer = isToPlanDrawerOpen && showEvents;
  const { t } = useTranslation('common', { keyPrefix: 'activityPlanning' });

  const { params } = useNavParams();
  const view = params?.view as View;

  const [planningEntityTypePreference] = usePlanningEntity();
  const planningEntityType = planningEntityTypePreference[view];

  const [
    isLoadingUnassignAppointment,
    setIsLoadingUnassignAppointmentFromEmployee
  ] = useState<boolean>(false);

  const { mutateAsync: unassignAppointmentAsync } =
    mutations.useRemoveAssignmentFromAppointments();

  const [payload, setPayload] = useState<
    PlanAppointmentsRequest | ConnectAppointmentGroupingPayload
  >();
  const [positionOffset, setPositionOffset] = useState<number>(0);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0
  });

  const [activeEvent, setActiveEvent] = useState<Event | null>(null);
  const [dndActiveState, setDndActiveState] = useState<Active | null>(null);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  const [pendingDropPosition, setPendingDropPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const selectedDayParts = useRecoilValue(selectedDayPartsAtom);
  const dateNavigationPeriod = useRecoilValue<Period | null>(
    dateNavigationPeriodAtom
  );

  const [dragActionActive, setDragActionActive] =
    useRecoilState<boolean>(dragActionActiveAtom);

  const [, setAppointmentDropPositionX] = useRecoilState(
    appointmentDropPositionXAtom
  );

  const selectedGroupingSchedule = useRecoilValue(selectedGroupingScheduleAtom);

  const [containerRef, { width: timelineRouteWidth }] = useElementSize([
    !!selectedGroupingSchedule,
    isLoading
  ]);

  const isTablet = useMediaQuery('(max-width: 1200px)');
  const [isDraggingTimeline, setIsDraggingTimeline] = useState<boolean>(false);
  const [dragParams, setDragParams] = useState<{
    x: number;
    y: number;
    isToPlan: boolean;
  }>({ x: 0, y: 0, isToPlan: false });

  const [, setCurrentVisibleDay] = useState<Date | null>(null);
  const [dayZoomLevel, setDayZoomLevel] = useState<ZoomLevel>(
    defaultDayTimelineZoomLevel ?? ZoomLevel.M
  );
  const [weekZoomLevel, setWeekZoomLevel] = useState<ZoomLevel>(
    defaultWeekTimelineZoomLevel ?? ZoomLevel.S
  );

  const [activeEntityContainingSelection, setActiveEntityContainingSelection] =
    useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const zoomLevel = useMemo(() => {
    return dateNavigationPeriod === 'week' ||
      dateNavigationPeriod === 'workweek'
      ? weekZoomLevel
      : dayZoomLevel;
  }, [dateNavigationPeriod, dayZoomLevel, weekZoomLevel]);

  const setZoomLevel = (level: ZoomLevel): void => {
    if (
      dateNavigationPeriod === 'week' ||
      dateNavigationPeriod === 'workweek'
    ) {
      setWeekZoomLevel(level);
    } else {
      setDayZoomLevel(level);
    }
  };

  const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5'
        }
      }
    })
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 5 } })
  );

  const onMutationError = () => {
    setPayload(undefined);
    setAppointmentDropPositionX({});
    revertAction();
  };

  const filterTimelineByDayPart = useCallback(
    (planningEntities: PlanningEntityMap) =>
      Object.values(planningEntities).filter(
        (props) =>
          props.type !== 'ORGANIZATIONAL_UNIT_TO_PLAN' &&
          (selectedDayParts.length === 0 ||
            selectedDayParts.includes(props.schedules?.[0]?.dayPart as DayPart))
      ),
    [selectedDayParts, planningEntityType]
  );

  const getFilteredEvents = () => {
    const filteredPlanningEntities = filterTimelineByDayPart(
      planningEntities || {}
    );

    if (!Object.keys(filteredPlanningEntities).length) return [];

    const schedules = Object.values(filteredPlanningEntities)
      .filter((entity) => entity.schedules)
      .map((entity) => entity.schedules as ScheduleResponse[])
      .flat();
    const events = schedules
      .map((schedule) => schedule.events as Event[])
      .flat();
    return events;
  };

  const getFilteredSchedules = (): ScheduleResponse[] => {
    const filteredPlanningEntities = filterTimelineByDayPart(
      planningEntities || {}
    );

    if (!Object.keys(filteredPlanningEntities).length) return [];

    return Object.values(filteredPlanningEntities)
      .filter((entity) => entity.schedules)
      .map((entity) => entity.schedules as ScheduleResponse[])
      .flat();
  };

  const getFilteredTimelineEvents = useMemo(
    () => [
      ...getFilteredEvents().map((event) => ({ ...event, droppable: true })),
      ...getFilteredSchedules()
    ],
    [planningEntities, selectedDayParts]
  );

  const interval = useTimelineInterval(getFilteredTimelineEvents, zoomLevel);
  const timeBarItemWidth = getTimeBarItemWidth(
    interval,
    scrollContainerRef.current?.clientWidth || 0
  );

  const timelineZoomWidth =
    timeBarItemWidth * (interval.timeWindow?.length || 0);

  const handlePlanAppointments = (payload: PlanAppointmentsRequest | null) => {
    handlePlanAppointmentsUtil({
      payload,
      planAppointments,
      setPayload,
      setShouldShowPopoverDialog,
      setDialogState,
      revertAction,
      onMutationError,
      planningEntitiesToEvents,
      getConflictingEvents,
      notificationsToHashTable,
      planningEntities,
      appendCorrelationToken,
      setPlanResponsePlanningEntityIdentifiers,
      onCancelWarningDialog: () => {
        setAppointmentDropPositionX({});
        setActiveEvent(null);
        setPendingDropPosition(null);
      },
      onSuccess: () => {
        setSelectedIds([]);
        setActiveEntityContainingSelection(null);
        setActiveEvent(null);
        setPendingDropPosition(null);
      },
      onComplete: () => {
        setTimeout(() => {
          setAppointmentDropPositionX({});
        }, 800);
        setSelectedIds([]);
      }
    });
  };

  const sendConnectAppointmentGrouping = useCallback(
    (
      appointmentGroupingEntityId: string,
      editAppointmentType?: EditAppointmentType,
      scheduleStart?: string
    ) => {
      if (planningEntities) {
        const payload: ConnectAppointmentGroupingPayload = {
          appointmentGroupingId: appointmentGroupingEntityId,
          appointmentGroupingStart: startDate,
          editAppointmentType: editAppointmentType || 'ONLY_THIS',
          suppressWarnings: false
        };
        setPayload(payload);
        const planningEntity = planningEntities[appointmentGroupingEntityId];
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);

        if (
          planningEntity.schedules
            .map((s) => s.events)
            .flat()
            .some((event) => event.isRecurring && !event.isAbsent) &&
          new Date(scheduleStart ?? startDate) >= today
        ) {
          setShouldShowPopoverDialog({
            id: appointmentGroupingEntityId,
            scheduleStart,
            isRouteConnect: true
          });
        } else {
          connectAppointmentGrouping(payload, {
            onSuccess: (response: any) => {
              if (!response.data.success) {
                const appointmentOccurrences =
                  planningEntities[
                    payload.appointmentGroupingId ?? ''
                  ].schedules
                    .map((s) => s.events)
                    .flat() ?? [];
                const notifications = response.data
                  .notifications as APNotification[];
                const conflictingEvents: Event[] = getConflictingEvents(
                  appointmentOccurrences || [],
                  notifications
                );

                setDialogState({
                  type: DialogType.WARNING,
                  open: true,
                  data: notificationsToHashTable(
                    conflictingEvents,
                    notifications
                  ),
                  onSubmit: () => {
                    connectAppointmentGrouping(
                      {
                        ...payload,
                        suppressWarnings: true
                      } as ConnectAppointmentGroupingPayload,
                      {
                        onError: onMutationError
                      }
                    );
                  },
                  onCancel: () => {
                    revertAction();
                    setAppointmentDropPositionX({});
                  }
                });

                setActiveEvent(null);
                setPendingDropPosition(null);
              }
            },
            onError: onMutationError
          });
        }
      }
    },
    [planningEntities]
  );

  const getDroppedTime = (positionX: number) => {
    const secondsPerPixel = getSecondsPerPixel(timeBarItemWidth, zoomLevel);
    const droppedTime = addSeconds(
      interval.start,
      (positionX - positionOffset) * secondsPerPixel
    );
    return droppedTime;
  };

  const onHandleAppointmentConfirmation = useCallback(
    (editAppointmentType: EditAppointmentType) => {
      if (shouldShowPopoverDialog.isUnassignment) {
        return unassignAppointment(editAppointmentType);
      } else if (shouldShowPopoverDialog.isRouteConnect) {
        const payload: ConnectAppointmentGroupingPayload = {
          appointmentGroupingId: shouldShowPopoverDialog.id,
          appointmentGroupingStart:
            shouldShowPopoverDialog.scheduleStart || startDate,
          editAppointmentType: editAppointmentType || 'ONLY_THIS',
          suppressWarnings: false
        };

        return connectAppointmentGrouping(payload, {
          onSuccess: (response: any) => {
            if (!response.data.success) {
              const appointmentOccurrences =
                planningEntities?.[
                  payload.appointmentGroupingId ?? ''
                ].schedules
                  .map((s) => s.events)
                  .flat() ?? [];
              const notifications = response.data
                .notifications as APNotification[];
              const conflictingEvents: Event[] = getConflictingEvents(
                appointmentOccurrences || [],
                notifications
              );

              setDialogState({
                type: DialogType.WARNING,
                open: true,
                data: notificationsToHashTable(
                  conflictingEvents,
                  notifications
                ),
                onSubmit: () => {
                  connectAppointmentGrouping(
                    {
                      ...payload,
                      suppressWarnings: true
                    } as ConnectAppointmentGroupingPayload,
                    {
                      onError: onMutationError
                    }
                  );
                },
                onCancel: () => {
                  revertAction();
                  setAppointmentDropPositionX({});
                }
              });
            } else {
              setActiveEvent(null);
              setPendingDropPosition(null);
            }

            setShouldShowPopoverDialog({ id: '' });
          },
          onError: onMutationError
        });
      }

      handlePlanAppointments({
        ...(payload as PlanAppointmentsRequest),
        editAppointmentType
      });
    },
    [payload, shouldShowPopoverDialog]
  );

  const onReset = () => {
    revertAction();
    setAppointmentDropPositionX({});
    setActiveEvent(null);
    setPendingDropPosition(null);
    setSelectedIds([]);
    setShouldShowPopoverDialog({
      id: '',
      isUnassignment: false,
      isRouteUnassignment: false,
      isRouteConnect: false
    });
  };

  function unassignAppointment(editAppointmentType: EditAppointmentType) {
    unassignAppointmentUtil({
      editAppointmentType,
      activeEvent,
      selectedIds,
      planningEntities,
      shouldShowPopoverDialog,
      appendCorrelationToken,
      unassignAppointmentAsync,
      setShouldShowPopoverDialog,
      setPlanResponsePlanningEntityIdentifiers,
      setActiveEntityContainingSelection,
      setIsLoading: setIsLoadingUnassignAppointmentFromEmployee,
      onFinally: () => {
        setIsLoadingUnassignAppointmentFromEmployee(false);
        setActiveEvent(null);
        setActiveEntityContainingSelection(null);
        setPendingDropPosition(null);
        setSelectedIds([]);
      },
      onGetEvents: (planningEntity, isToPlan, selectedIds) => {
        return (
          (isToPlan
            ? planningEntity?.planningTasks
                ?.map((task: any) => task.data)
                .filter(
                  (event: Event) =>
                    event?.id !== undefined && selectedIds.includes(event.id)
                )
            : planningEntity?.schedules
                .flatMap((s: any) => s.events)
                .filter(({ id }: { id: string }) => selectedIds.includes(id))
          )?.filter(
            (event: Event | undefined): event is Event => event !== undefined
          ) || []
        );
      }
    });
  }

  function scrollTimeline(direction: 'forward' | 'backward') {
    const { clientWidth, scrollLeft } = (scrollContainerRef.current || {
      clientWidth: 0,
      scrollLeft: 0,
      scrollWidth: 0
    }) as HTMLDivElement;
    const x = scrollLeft || 0;
    const jumpWidth = (clientWidth || 0) * 0.5;

    scrollContainerRef.current?.scrollTo({
      left: direction === 'forward' ? x + jumpWidth : x - jumpWidth,
      behavior: 'smooth'
    });
  }

  function dragTimeline(e: ReactMouseEvent) {
    const startPos = scrollContainerRef.current?.scrollLeft || 0;
    const movement = e?.movementX || 0;
    const speed = 1.5;

    scrollContainerRef.current?.scrollTo({ left: startPos - movement * speed });
  }

  function scrollToDay(selectedDate: Date, routeId?: string) {
    if (!scrollContainerRef.current) return;

    const secondsPerPixel = getSecondsPerPixel(timeBarItemWidth, zoomLevel);
    const timeDifferenceInSeconds =
      (selectedDate.getTime() - interval.start.getTime()) / 1000;

    // Calculate the horizontal scroll position
    const horizontalScrollPosition = timeDifferenceInSeconds / secondsPerPixel;

    // Calculate the vertical scroll position
    let verticalScrollPosition = 0;

    if (routeId) {
      const routeElement = document.querySelector(
        `.__${routeId}__`
      ) as HTMLElement | null;
      if (routeElement) {
        verticalScrollPosition =
          routeElement.offsetTop - scrollContainerRef.current.offsetTop - 50; // 50px offset

        // Highlight the route for a few seconds
        routeElement.classList.add('highlighted');
        setTimeout(() => {
          routeElement.classList.remove('highlighted');
        }, 4000);
      }
    }

    // Scroll both directions simultaneously
    scrollContainerRef.current.scrollTo({
      left: horizontalScrollPosition,
      top: verticalScrollPosition,
      behavior: 'smooth'
    });
  }

  // Reset to defaults upon perspective change
  useEffect(() => {
    setActiveEvent(null);
    setPendingDropPosition(null);
    setSelectedIds([]);
    setAppointmentDropPositionX({});
    setPayload(undefined);
  }, [planningEntityType]);

  return (
    <DndContext
      autoScroll={{
        activator: AutoScrollActivator.Pointer,
        order: TraversalOrder.ReversedTreeOrder,
        threshold: {
          x: 0.25,
          y: 0.25
        },
        acceleration: 7,
        canScroll: (element: Element) =>
          element.classList.contains(TIMELINE_DRAGGABLE_SCROLL_CONTAINER_CLASS)
      }}
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      collisionDetection={pointerWithin}
      modifiers={[]}
    >
      <PopOverConfirmationContext.Provider
        value={{
          onHandleAppointmentConfirmation,
          onClose: onReset,
          isLoading
        }}
      >
        <Box display="flex" overflow="hidden">
          <Box
            sx={{
              width: '100%',
              overflowX: 'scroll',
              height: '100%',
              minWidth: taskListIsOpen ? 'initial' : '100%',
              maxWidth: taskListIsOpen
                ? `calc(100% - ${TASK_LIST_WIDTH}px)`
                : 'initial',
              position: 'relative',
              borderLeft: `1px solid ${GRAY}`,
              background: GRAY_LIGHT_SECONDARY
            }}
          >
            {/* Fixed current day flag */}
            {/* TODO: replace deprecated useIntersectionObserver hook in timewindow with vanilla JS version and uncomment */}
            {/*{currentVisibleDay && (*/}
            {/*  <Box*/}
            {/*    sx={{*/}
            {/*      position: 'absolute',*/}
            {/*      zIndex: 50,*/}
            {/*      top: '4px',*/}
            {/*      left: `${TIMELINE_SUMMARY_WIDTH + 8}px`,*/}
            {/*      height: '16px',*/}
            {/*      width: '100px',*/}
            {/*      display: 'inline-block',*/}
            {/*      paddingLeft: '10px',*/}
            {/*      backgroundColor: GRAY_LIGHT_SECONDARY*/}
            {/*    }}*/}
            {/*  >*/}
            {/*    <DayFlag time={currentVisibleDay} sx={{ top: 0, left: 0 }} />*/}
            {/*  </Box>*/}
            {/*)}*/}

            <Box display="flex" flexDirection="column" pl={2}>
              <Box
                ref={scrollContainerRef}
                className={TIMELINE_DRAGGABLE_SCROLL_CONTAINER_CLASS}
                flexGrow="1"
                display="flex"
                flexDirection="row"
                onMouseDown={() => setIsDraggingTimeline(true)}
                onMouseUp={() => setIsDraggingTimeline(false)}
                onMouseLeave={() => setIsDraggingTimeline(false)}
                onMouseMove={(e: ReactMouseEvent) => {
                  if (!isDraggingTimeline || dragActionActive) return;
                  dragTimeline(e);
                }}
                sx={{
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  height: `calc(100vh - ${isTablet ? 260 : 210}px)` // Minus headers
                }}
              >
                <Box width="100%">
                  <Stack pb={4} position="relative" zIndex={0}>
                    {isValid(interval?.start) && isValid(interval?.end) && (
                      <TimelineBar
                        innerRef={containerRef}
                        interval={interval}
                        zoomLevel={zoomLevel}
                        scrollContainerRef={scrollContainerRef}
                        timeBarItemWidth={timeBarItemWidth}
                        toPlanDrawerIsOpen={showToPlanDrawer}
                        onChangeZoomLevel={(level: ZoomLevel) => {
                          setZoomLevel(level);
                          dispatch(
                            setUserPreference({
                              key:
                                dateNavigationPeriod === 'week' ||
                                dateNavigationPeriod === 'workweek'
                                  ? 'defaultWeekTimelineZoomLevel'
                                  : 'defaultDayTimelineZoomLevel',
                              value: level
                            })
                          );
                        }}
                        onChangeCurrentDay={(day: Date) =>
                          setCurrentVisibleDay(day)
                        }
                        onNext={() => scrollTimeline('forward')}
                        onPrevious={() => scrollTimeline('backward')}
                      />
                    )}
                    <Box mt={2}>
                      {Boolean(
                        interval?.durationInSeconds && isValid(interval?.start)
                      ) &&
                        !!planningEntities &&
                        filterTimelineByDayPart(planningEntities)
                          .sort(({ name: a }, { name: b }) =>
                            planningEntityType === 'EMPLOYEE'
                              ? (a || '').localeCompare(b || '')
                              : 0
                          )
                          .map((props) => {
                            return (
                              <Box
                                key={props.id ?? ''}
                                display="flex"
                                minWidth={`${
                                  timelineZoomWidth + TIMELINE_SUMMARY_WIDTH
                                }px`}
                                width="100%"
                                marginTop="10px"
                              >
                                <Box
                                  sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 20,
                                    display: 'flex',
                                    alignItems: 'center',
                                    minHeight: '65px',
                                    margin: '-5px 0', // Too cover box-shadow of routes
                                    background: `linear-gradient(90deg, ${GRAY_LIGHT_SECONDARY} 80%, transparent)`
                                  }}
                                >
                                  <Summary name={props.name} />
                                </Box>

                                <TimelineRoute
                                  interval={interval}
                                  positionOffset={positionOffset}
                                  timelineRouteWidth={timelineZoomWidth}
                                  timeBarItemWidth={timeBarItemWidth}
                                  planningEntity={props}
                                  activeEntityContainingSelection={
                                    activeEntityContainingSelection
                                  }
                                  zoomLevel={zoomLevel}
                                  onChangeMousePosition={setMousePosition}
                                  onChangeActiveEntity={
                                    setActiveEntityContainingSelection
                                  }
                                  onChangeSelection={setSelectedIds}
                                  connectAppointmentGrouping={
                                    sendConnectAppointmentGrouping
                                  }
                                />
                              </Box>
                            );
                          })}
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Box>
          </Box>

          <TaskList
            onOpen={setTaskListIsOpen.bind(undefined, true)}
            onClose={setTaskListIsOpen.bind(undefined, false)}
            planningEntities={planningEntities}
            dropzonePadding="10px"
            activeEntityContainingSelection={activeEntityContainingSelection}
            onChangeActiveEntity={setActiveEntityContainingSelection}
            onChangeSelection={setSelectedIds}
            onChangeMousePosition={setMousePosition}
            onRouteClick={scrollToDay}
            isTimeline
          />
        </Box>
        {activeEvent && (
          <DragPortal
            activeEvent={activeEvent}
            selectedIds={selectedIds}
            pendingDropPosition={pendingDropPosition}
            dropAnimation={dropAnimation}
            isTimeline={true}
            timelineRouteWidth={timelineRouteWidth}
            interval={interval}
            mousePosition={mousePosition}
            dragParams={dragParams}
          />
        )}
      </PopOverConfirmationContext.Provider>
      <LoadingOverlay
        loading={isLoading || isLoadingUnassignAppointment}
        zIndex={60}
      />
    </DndContext>
  );

  function handleDragCancel() {
    setDragActionActive(false);
    onReset();
  }

  function handleDragStart(event) {
    setDragActionActive(true);
    if (!planningEntities) return;
    const { active } = event;
    const { id } = active;
    const aid = id === 'multiple' ? selectedIds[0] : id;

    const activePlanningEntityId = active?.data.current.planningEntityId;
    const isToPlan = active?.data.current.isToPlan;

    const activeEvent: Event | null | undefined = !isToPlan
      ? planningEntities?.[activePlanningEntityId].schedules
          .map((s) => s.events)
          .flat()
          .find((event): event is Event => event?.id === aid) ?? null
      : planningEntities?.[activePlanningEntityId].planningTasks
          ?.map((task) => task.data)
          .find((event): event is Event => (event as Event)?.id === aid) ??
        null;

    if (!activeEvent) return;
    setActiveEvent(activeEvent);
    setDndActiveState(active);
    setActiveEntityContainingSelection(active.data.current.planningEntityId);

    const rect = document.getElementById(aid)?.getBoundingClientRect();
    if (!rect) return;

    const chipWasActivityCard = isToPlan;
    const activityCardOffset = chipWasActivityCard
      ? 0 - (rect.left - mousePosition.x)
      : 0;
    const offsetX = mousePosition.x - rect.left - activityCardOffset;
    const offsetY = mousePosition.y - rect.top;

    setDragParams({ x: offsetX, y: offsetY, isToPlan });
    setPositionOffset(offsetX);

    if (selectedIds.length > 0) {
      setSelectedIds((selected) => {
        if (selected.includes(id)) {
          return selected;
        }
        return [];
      });

      return;
    }
  }

  function handleDragEnd({ over, delta }) {
    setDragActionActive(false);

    const active = dndActiveState;

    if (
      !active ||
      !active?.data?.current ||
      Object.keys(active.data.current).length === 0
    ) {
      return showSnackbar({
        severity: 'warning',
        message: t('dragActionFailed')
      });
    }

    const overId = over?.id;
    const overPlanningEntityId = over?.data.current.planningEntityId;
    const activePlanningEntityId = active?.data?.current?.planningEntityId;
    const activeIsToPlan = active?.data.current.isToPlan;
    const overIsToPlan = over?.data.current.isToPlan;

    if (
      !over ||
      (activeIsToPlan === overIsToPlan && overId === 'TO_PLAN_DROPZONE')
    )
      return setPendingDropPosition(null);

    const mouseUpHandler = (e: MouseEvent) => {
      setPendingDropPosition({
        x: e.clientX,
        y: e.clientY
      });

      // Remove the event listener after it has been triggered
      window.removeEventListener('mouseup', mouseUpHandler);
    };

    window.addEventListener('mouseup', mouseUpHandler, { once: true });

    if (active.id === overId) return;

    if (!activeEvent) return onReset();

    if (
      activePlanningEntityId === undefined ||
      overId === undefined ||
      (activeEvent === null && selectedIds.length === 0) ||
      (activeIsToPlan && overIsToPlan)
    ) {
      onReset();
      return;
    }

    if (overPlanningEntityId === activePlanningEntityId && !activeIsToPlan) {
      const originalTime = format(new Date(activeEvent.start), 'HH:mm');
      const newTime = format(
        getDroppedTime(over.data.current.position.x),
        'HH:mm'
      );

      if (originalTime === newTime) {
        return;
      }
    }

    const ids = selectedIds.length ? selectedIds : [active.id as string];
    const count = selectedIds.length;

    const selectedEvents: Event[] = ids.map(
      (id) => findItem(planningEntities, activePlanningEntityId, id) as Event
    );

    const MULTI_SELECT_ID = 'multiple';

    let start;
    if (overIsToPlan) {
      start = null;
    } else {
      const droppedTime = getDroppedTime(over.data.current.position.x);
      start = droppedTime;
    }

    const occurrenceDate = start
      ? format(new Date(start), 'y-MM-dd')
      : startDate;

    const payload: PlanAppointmentsRequest = {
      appointmentOccurrences:
        selectedEvents.length === 0
          ? [
              {
                entityId: activeEvent.id,
                originalOccurrenceStart: activeEvent.start,
                schedulingLabel: activeEvent.schedulingLabel
              }
            ]
          : selectedEvents.map((event: Event) => ({
              entityId: event.id,
              originalOccurrenceStart: event.start,
              schedulingLabel: event.schedulingLabel
            })),
      planningEntity: {
        ...over.data.current.planningEntityIdentifier,
        start: over.data.current.scheduleStart
      },
      editAppointmentType: 'ONLY_THIS',
      suppressWarnings: false,
      start: start
    };

    setPayload(payload);

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const scrollToPlanIntoView = () =>
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView();
      }, 150);

    const id = count > 1 ? MULTI_SELECT_ID : activeEvent.id;

    if (new Date(occurrenceDate) < today) {
      if (overIsToPlan) {
        unassignAppointment('ONLY_THIS');
        scrollToPlanIntoView();
        return;
      } else {
        return handlePlanAppointments(payload);
      }
    }

    if (activeEvent?.isRecurring && new Date(occurrenceDate) >= today) {
      setShouldShowPopoverDialog({
        id: activeEvent.id,
        scheduleStart: over.data.current.scheduleStart,
        isUnassignment: overIsToPlan,
        isDragAction: true
      });
    } else if (
      !selectedEvents.some((item) => item.isRecurring) &&
      overIsToPlan
    ) {
      unassignAppointment('ONLY_THIS');
      scrollToPlanIntoView();
    } else {
      handlePlanAppointments(payload);
    }

    const startingPoint = getStartingPoint(
      activeEvent?.start,
      interval,
      zoomLevel,
      timeBarItemWidth
    );

    setAppointmentDropPositionX({
      [id]: activeIsToPlan
        ? over.data.current.position.x - positionOffset
        : overIsToPlan
        ? 0
        : delta.x + startingPoint
    });

    setPositionOffset(0);
    setDndActiveState(null);
  }
}
